import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from '../../../composants/Button/Button';
import ValidationPopUp from '../../../composants/ValidationPopUp/ValidationPopUp';
import Banner from '../../../composants/Banner/Banner';
import SectionPliable from '../../../composants/SectionPliable/SectionPliable';
import { PrintButton } from '../../../composants/PrintButton/PrintButton';
import DocumentsList from '../../../composants/DocumentsList/DocumentsList';
import ListePiecesCard from '../../../composants/ListePiecesCard/ListePiecesCard';
import { adresseEntiteUneLigne, adresseUneLigne } from '../../../utils/adresse';
import { loadContratTitulaire, titreContratTitulaire } from '../../../services/contratTitulaireService';
import type { ContratTitulaire } from '../../../services/contratTitulaireService';
import TitulairesLotCards from '../../../composants/TitulairesLotCards/TitulairesLotCards';
import { useDocuments } from '../../../contexts/DocumentsContext';
import { formatPrice, getStatusColor } from '../../../utils/entityUtils';
import { useLotImage } from '../../../utils/getLotImage';
import { CommandeBC } from '../types';
import {
  formatCommandeArticles,
  totalArticlesHT,
  isCommandeExpired,
  prestataireHasActed,
  canAccepterRefuser,
  canPrestataireEditCatalogue,
  computePrestataireStatusBanner,
  buildAdminViewFormData,
} from '../domain/commandeConsultation';
// Import intra-module : l'onglet Consultation affiche le MEME etat de refus que l'onglet Offre
// (doublon assume), il consomme donc le meme calcul et le meme texte plutot qu'une recopie.
import { computeOffreState, messageRefusPrestataire } from '../domain/commandeOffre';
import { libelleTypeCommande } from '../domain/complements';
import { computeConsultationNextTabCta } from '../domain/commandeNextTab';
import NextTabButton from '../../../composants/NextTabButton/NextTabButton';
import { refuserCommande } from '../services/commandeBcService';
import '../../../composants/FormulaireAdministratif/FormulaireAdministratif.css';
// Feuille generique partagee (meme import que la case acheteur OperationConsultation) : porte
// .articles-table-wrapper / .article-name / .total-section / .no-articles-message etc. -> rendu
// identique. CommandeConsultation.css porte les classes presta + la grille .bcc-desc* du descriptif.
import '../../../composants/OperationConsultationBC/OperationConsultationBC.css';
import './CommandeConsultation.css';
import { MENTION_VALIDITE_ACCORD_CADRE_BC } from '../../../utils/mentionsLegalesBC';

const PLACEHOLDER = '-';

// Champ LECTURE SEULE (label + valeur, "-" si vide), span de colonnes sur la grille 12-col .bcc-desc__grid.
// Copie ISOLEE du ChampRO de la page MS (pas d'import cross-module).
const ChampRO: React.FC<{ label: string; value?: string | number | null; col?: number }> = ({
  label,
  value,
  col = 3,
}) => {
  const texte = value === 0 ? '0' : value ? String(value) : '';
  return (
    <div className="form-group" style={{ gridColumn: `span ${col}` }}>
      <label title={label}>{label}</label>
      <div className="input-inactif">{texte.trim() ? texte : PLACEHOLDER}</div>
    </div>
  );
};

interface CommandeConsultationProps {
  commande: CommandeBC;
  refetch: () => Promise<void> | void;
  // Bascule vers l'onglet OFFRE : c'est la que vivent la signature (accept/transfer) et le
  // remplissage du catalogue (panier inverse). Consultation ne monte PAS de SignatureFlow.
  onGoToOffre?: () => void;
}

// Onglet Consultation PRESTATAIRE-BC (V2). Miroir LECTURE SEULE du descriptif defini par l'acheteur,
// aligne sur la refonte de la consultation MS (memes sections : Descriptif Administratif / Technique /
// Financier / procedural). ISOLE de MS : aucun import depuis SuiviOperationMSV2 ni SuiviDeCommandeMSV2 ;
// tout le rendu (grille .bcc-desc*, champs) est co-localise ici. Les champs de la refonte que la donnee
// BC ne porte pas encore s'affichent en "-" (l'operation BC est un sous-ensemble de l'operation MS ;
// ils se rempliront quand le backend BC suivra). Le Descriptif Financier est INCHANGE (articles + prix
// + rang, panier inverse renvoye vers l'onglet Offre).
const CommandeConsultation: React.FC<CommandeConsultationProps> = ({
  commande,
  refetch,
  onGoToOffre,
}) => {
  const printRef = useRef<HTMLDivElement>(null);
  const lotImage = useLotImage(commande.operation?.lot_technique?.nom);

  // L'expiration peut survenir en direct : le chrono partage emet 'chronoExpired' a zero.
  const [chronoExpired, setChronoExpired] = useState(false);
  useEffect(() => {
    const handler = () => setChronoExpired(true);
    window.addEventListener('chronoExpired', handler);
    return () => window.removeEventListener('chronoExpired', handler);
  }, []);

  // Etats UI (uniquement le refus est actionnable depuis Consultation).
  const [isRefuseModalOpen, setIsRefuseModalOpen] = useState(false);
  const [isRefusing, setIsRefusing] = useState(false);
  const [refuseSuccess, setRefuseSuccess] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  // --- Derivations PURES (domaine) -------------------------------------------
  const expired = useMemo(() => isCommandeExpired(commande, chronoExpired), [commande, chronoExpired]);
  const adminFormData = useMemo(() => buildAdminViewFormData(commande), [commande]);
  const articles = useMemo(() => formatCommandeArticles(commande), [commande]);
  const totalHT = useMemo(() => totalArticlesHT(articles), [articles]);
  const statusBanner = useMemo(
    () => computePrestataireStatusBanner(commande, chronoExpired),
    [commande, chronoExpired],
  );
  // Date/heure du dernier changement de statut (refus / acceptation), pour les bannieres.
  const statusDate = commande.updated_at ? new Date(commande.updated_at) : null;
  const statusDateStr =
    statusDate && !Number.isNaN(statusDate.getTime())
      ? `${statusDate.toLocaleDateString('fr-FR')} à ${statusDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
      : '';
  const hasActed = useMemo(() => prestataireHasActed(commande), [commande]);
  const showAccepterRefuser = useMemo(
    () => canAccepterRefuser(commande, chronoExpired),
    [commande, chronoExpired],
  );
  // Panier inverse "A chiffrer" : le remplissage + l'envoi se font dans l'onglet OFFRE.
  const isChiffrage = useMemo(
    () => canPrestataireEditCatalogue(commande, chronoExpired),
    [commande, chronoExpired],
  );
  // CTA de bascule vers l'onglet Offre. Le libelle suit la FENETRE DE DEPOT (« Déposer mon offre »
  // tant qu'elle est ouverte, sinon « Voir l'offre »), pas le type de panier. `onGoToOffre` est
  // undefined exactement quand l'onglet Offre est ferme (cf. CommandeContent.goTo) : c'est le meme
  // predicat d'atteignabilite que la barre d'onglets.
  const nextTabCta = useMemo(
    () => computeConsultationNextTabCta(commande, chronoExpired, !!onGoToOffre),
    [commande, chronoExpired, onGoToOffre],
  );

  const op = commande.operation || {};
  const besoin = commande.formulaire?.besoin || op.besoin || PLACEHOLDER;
  const rang = commande.rang ?? 1;

  // Date/heure limite de depot de l'offre (format "JJ/MM/AAAA à HH:mm") pour le bandeau d'invitation.
  const dateLimiteDepot = commande.expire_at
    ? `${new Date(commande.expire_at).toLocaleDateString('fr-FR')} à ${new Date(
        commande.expire_at,
      ).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
    : null;

  // Contact operationnel de l'acheteur (present sur l'operation BC via operation.acheteur).
  const contactAcheteur = [op.acheteur?.prenom, op.acheteur?.nom].filter(Boolean).join(' ');

  // Organisme acheteur (enrichi par le gateway) : SIRET / type / adresse du siege social.
  const orga = op.organisme;
  // Adresse du siege social sur UNE ligne, code postal / ville / pays compris : ils n'ont plus de
  // champ propre a cote. Le prestataire lit exactement la meme forme que l'acheteur.
  const adresseSiege = adresseEntiteUneLigne(orga?.adresse);

  // Lots (procedural) : deux libelles "code - nom".
  const lotTechnique = op.lot_technique ? `${op.lot_technique.id ?? ''} - ${op.lot_technique.nom ?? ''}` : '';
  const departement = op.lot_departement?.departement;
  const lotGeographique = departement ? `${departement.id ?? ''} - ${departement.nom ?? ''}` : '';

  // Pieces de l'accord-cadre en vigueur (CCAP/CCTP...), chargees app-wide par le DocumentsProvider
  // (monte globalement dans App.tsx). Meme mapping que cote MS/acheteur -> format DocumentsList.
  const { commonDocuments, loading: acDocsLoading } = useDocuments();

  // Son contrat accord-cadre, en tete des pieces. Pas de recherche par rang ici, contrairement a
  // l'acheteur : la commande est deja scopee au rang du prestataire, `id_contrat` est donc le sien.
  const [contratTitulaire, setContratTitulaire] = useState<ContratTitulaire | null>(null);
  const lotContratId = commande.id_contrat;

  useEffect(() => {
    if (!lotContratId) {
      setContratTitulaire(null);
      return undefined;
    }
    let annule = false;
    loadContratTitulaire(lotContratId)
      .then((contrat) => {
        if (!annule) setContratTitulaire(contrat);
      })
      .catch(() => {
        // Piece d'appoint : son echec ne doit pas priver le presta des CCTP, qui eux sont la.
        if (!annule) setContratTitulaire(null);
      });
    return () => {
      annule = true;
    };
  }, [lotContratId]);

  const piecesAccordCadre = useMemo(() => {
    const communs = commonDocuments
      .filter((d) => d.actif)
      .map((d) => ({
        id: d.id,
        type: d.type,
        updated_at: d.updated_at,
        displayLabel: d.nom,
        category: 'commun' as const,
      }));

    // Sans binaire, pas de carte : mieux vaut ne rien afficher qu'un bouton qui telechargerait du vide.
    if (!contratTitulaire?.binaire) return communs;

    return [
      {
        id: contratTitulaire.id,
        type: 'Contrat',
        updated_at: contratTitulaire.updated_at ?? '',
        displayLabel: titreContratTitulaire(contratTitulaire),
        category: 'accord_cadre' as const,
        // `key` distingue cette carte des documents communs, identifies par leur seul `id` : sans elle,
        // un document commun de meme id serait confondu avec le contrat.
        key: `contrat-${contratTitulaire.id}`,
        binaire: contratTitulaire.binaire,
      },
      ...communs,
    ];
  }, [commonDocuments, contratTitulaire]);

  // --- Refus (panier normal) -------------------------------------------------
  const handleConfirmRefus = async () => {
    setInlineError(null);
    setIsRefusing(true);
    try {
      await refuserCommande(commande.id); // id-based, jamais window.location
      setRefuseSuccess(true);
    } catch (error) {
      setInlineError(error instanceof Error ? error.message : "Erreur lors du refus de l'opération");
      setIsRefuseModalOpen(false);
    } finally {
      setIsRefusing(false);
    }
  };

  const handleCloseRefusSuccess = async () => {
    setIsRefuseModalOpen(false);
    setRefuseSuccess(false);
    await refetch(); // refetch via prop, jamais window.location.reload()
  };

  const subtitle = (() => {
    const lt = op.lot_technique;
    const dep = op.lot_departement?.departement;
    return lt && dep ? `${lt.id} - ${lt.nom}, ${dep.id} - ${dep.nom}` : 'Informations de lot non disponibles';
  })();

  const dateFr = (value?: string): string => (value ? new Date(value).toLocaleDateString('fr-FR') : '');
  const dateTimeFr = (value?: string | null): string => {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime())
      ? ''
      : `${d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })} (GMT Paris)`;
  };

  // Invitation a deposer l'offre avant l'echeance : presta, quel que soit le mode (panier normal
  // ou inverse a chiffrer), tant que l'action est encore possible. Rendue en HAUT et en BAS
  // (avant les boutons d'action).
  const depotBanner =
    (showAccepterRefuser || isChiffrage) && dateLimiteDepot ? (
      <Banner
        variant="warning"
        message={`Vous êtes invités à déposer votre offre jusqu'au ${dateLimiteDepot}`}
      />
    ) : null;

  const offreState = computeOffreState(commande, chronoExpired);

  // Bannieres d'ETAT, definies UNE fois et rendues en HAUT ET EN BAS de l'onglet.
  //
  // Le refus passe par `offreState` (le meme calcul que l'onglet Offre) et non par `statusBanner` :
  // seul `refusInfo` porte l'AUTEUR du refus. `statusBanner` ne connait que le statut, ce qui
  // conduisait a affirmer « Vous avez refusé de faire une offre » y compris quand le refus venait
  // de l'acheteur. Message produit par le domaine, partage avec l'onglet Offre.
  const stateBanners = (
    <>
      {offreState.showRefusMessage && offreState.refusInfo && (
        <Banner variant="danger" filled message={messageRefusPrestataire(offreState.refusInfo)} />
      )}
      {statusBanner.visible && statusBanner.variant === 'accepted' && (
        <Banner
          variant="success"
          filled
          message={`Bon de commande accepté et signé${statusDateStr ? ` le ${statusDateStr}` : ''}`}
        />
      )}
    </>
  );

  return (
    <div className="consultation-bc" ref={printRef}>
      {depotBanner}
      {expired && (
        <div className="cmd-consultation__expired-notice">
          Cette commande est expirée. Vous ne pouvez plus agir sur cette consultation.
        </div>
      )}

      {/* Refus (gris) / acceptation (vert) -> composant Banner avec date ; autres -> badge inline. */}
      {stateBanners}
      {statusBanner.visible && statusBanner.variant !== 'refused' && statusBanner.variant !== 'accepted' && (
        <div className={`cmd-consultation__status cmd-consultation__status--${statusBanner.variant}`}>
          {statusBanner.message}
        </div>
      )}

      {/* 1) Descriptif Administratif : 3 sous-blocs (Acheteur / Operation / Particularites),
          lecture seule, parite refonte MS. Champs sans donnee BC -> "-". */}
      <SectionPliable title="Descriptif Administratif" defaultOpen>
        <div className="section-content">
          <div className="bcc-desc">
            <section className="bcc-desc__sous-section">
              <h4 className="bcc-desc__sous-section-titre">Acheteur</h4>
              <div className="bcc-desc__grid">
                <ChampRO label="Nom de l'organisme" value={orga?.nom} col={6} />
                <ChampRO label="Siret" value={orga?.siret} />
                <ChampRO label="Type d'organisme" value={orga?.type_organisme} />
                <ChampRO label="Adresse du siège social" value={adresseSiege} col={12} />
                <ChampRO label="Nom du contact opérationnel" value={contactAcheteur} />
                <ChampRO label="Fonction" value="" />
                <ChampRO label="Mail" value={op.acheteur?.email_contact} />
                <ChampRO label="Téléphone" value={op.acheteur?.telephone} />
                <ChampRO label="Nom du signataire qui apparaîtra sur la commande" value={op.signataire_commande} col={6} />
                <ChampRO label="Modalité de facturation" value={op.facturation} col={6} />
              </div>
            </section>

            <section className="bcc-desc__sous-section">
              <h4 className="bcc-desc__sous-section-titre">Opération</h4>
              <div className="bcc-desc__grid">
                <ChampRO label="Nom de l'ouvrage" value={op.nom_ouvrage} />
                <ChampRO label="Nom de l'opération" value={adminFormData.nomOperation} />
                <ChampRO label="N° Référence interne" value={adminFormData.referenceInterne} />
                {/* Type de commande : derive de operation.type_commande (Initiale, ou « Complémentaire de
                    <numero de la principale> »). */}
                <ChampRO label="Type de commande" value={libelleTypeCommande(commande.operation?.type_commande)} />
                {/* Adresse de l'operation sur UNE ligne, code postal / ville / pays compris : le
                    prestataire lit exactement la forme que l'acheteur a saisie et relit. */}
                <ChampRO
                  label="Adresse de l'opération"
                  value={adresseUneLigne(adminFormData.adresse, adminFormData.codePostal, adminFormData.ville, op.pays)}
                  col={12}
                />
                <ChampRO label="Cabinet de maitrise d'œuvre éventuel ?" value={adminFormData.coordonneesMaitrise} />
                <ChampRO label="Interlocuteur" value={op.moe_interlocuteur} />
                <ChampRO label="Mail" value={op.moe_mail} />
                <ChampRO label="Téléphone" value={op.moe_telephone} />
                <ChampRO label="Date prévisionnelle de démarrage" value={dateFr(adminFormData.dateDebutOperation)} />
                <ChampRO
                  label="Durée prévisionnelle (jours calendaires)"
                  value={
                    adminFormData.dateDebutOperation && adminFormData.dateFinOperation
                      ? `${adminFormData.delaisFinOperation} jours`
                      : ''
                  }
                />
                <ChampRO label="Date prévisionnelle de fin d'opération" value={dateFr(adminFormData.dateFinOperation)} />
                <ChampRO label="Phases ?" value={op.phases} />
              </div>
            </section>

            <section className="bcc-desc__sous-section">
              <h4 className="bcc-desc__sous-section-titre">Particularités administratives</h4>
              <div className="bcc-desc__grid">
                <ChampRO label="Niveau de sensibilité des informations liées à cette opération ?" value={op.niveau_sensibilite} col={6} />
                <ChampRO label="Visite préalable obligatoire ?" value={op.condition_visite} col={6} />
                <ChampRO label="Vérification préalable des identités pour accéder à l'opération ?" value={op.verification_identites} col={6} />
                <ChampRO label="Habilitation particulière à présenter ?" value={op.habilitation} col={6} />
                <ChampRO label="Charte et/ou réglementation particulière imposée sur cette opération ?" value={op.charte_reglementation} col={6} />
                <ChampRO label="Clause de suivi environnemental et social ?" value={op.clause_suivi_environnemental} col={6} />
              </div>
            </section>
          </div>
        </div>
      </SectionPliable>

      {/* 2) Descriptif Technique : attestation eventuelle + documentation + cahier des charges.
          Les 3 champs documentaires (URL / modalite / liste) n'existent pas encore cote BC -> "-". */}
      <SectionPliable title="Descriptif Technique" defaultOpen>
        <Banner
          variant="info"
          message="Les informations écrites ci-dessous sont contractuelles y compris les pièces éventuellement chargées au travers du lien URL. Mais, toutes précisions administratives ou juridiques de nature à contrevenir à l'accord-cadre général sans dérogations prévues au contrat sont à considérer comme inexistantes dans ce descriptif"
        />
        <div className="section-content">
          <div className="bcc-desc">
            <div className="bcc-desc__grid bcc-desc__grid--doc" style={{ marginBottom: '1.6rem' }}>
              <ChampRO label="URL vers la doc technique et/ou planning détaillé" value={op.url_doc_technique} col={4} />
              <ChampRO label="Modalité d'accès à la documentation" value={op.modalite_acces_documentation} col={4} />
              <ChampRO label="Liste des pièces éventuelles composant la documentation technique" value={op.liste_pieces_documentation} col={4} />
            </div>
            <div className="form-group">
              <label>Cahier des charges techniques et particularités à connaitre</label>
              <div className="input-inactif bcc-desc__besoin-readonly">{besoin}</div>
            </div>
          </div>
        </div>
      </SectionPliable>

      {/* 3) Descriptif Financier : INCHANGE. Tableau articles read-only (avec prix + rang).
           En mode panier inverse "A chiffrer", renvoi vers l'onglet OFFRE. */}
      <SectionPliable title="Descriptif Financier" defaultOpen>
        <div className="section-content">
          {isChiffrage ? (
            // Pas de bouton ici : la bascule vers l'onglet Offre est le CTA de la barre d'actions du
            // bas (« Déposer mon offre »). En doubler un second orange vers la MEME destination
            // depuis cette section donnait deux CTA concurrents sur l'onglet, dont un au libelle
            // hors-spec (« Aller à l'onglet Offre »).
            <div className="no-articles-message">
              <p>
                Cette consultation est en mode « À chiffrer » : la saisie de votre catalogue et
                l’envoi de votre offre se font dans l’onglet « Offre ».
              </p>
            </div>
          ) : articles.length > 0 ? (
            <div className="articles-display">
              <div className="articles-table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>N° Réf</th>
                      <th>Description de la prestation</th>
                      <th>Unité</th>
                      <th>Quantité</th>
                      <th>Prix unitaire HT</th>
                      <th>Total HT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {articles.map((article) => (
                      <tr key={article.catalogueId || article.id}>
                        <td>{article.code}</td>
                        <td>{article.nom}</td>
                        <td>{article.unite}</td>
                        <td>{article.quantite}</td>
                        <td>{formatPrice(article.prix, 4, true)}</td>
                        <td>{formatPrice(article.totalHT, 4, true)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="total-section">
                <p>
                  Total HT (Rang {rang}) : {formatPrice(totalHT, 4, true)}
                </p>
              </div>
            </div>
          ) : (
            <div className="no-articles-message">
              <p>Aucun article défini pour cette consultation.</p>
            </div>
          )}
        </div>
      </SectionPliable>

      {/* 4) Descriptif procédural : recap lecture seule (parite refonte MS), SANS les champs exclus
          (ouverture des plis, notification, budget, origine du financement, module Q/R, criteres).
          Le prestataire ne voit QUE son rang (commande deja scopee a commande.rang). */}
      <SectionPliable title="Descriptif procédural" defaultOpen>
        {/* Portee de la date de validite affichee juste en dessous : un bon notifie avant le terme
            de l'accord-cadre reste executable apres. Texte reglementaire partage (une seule
            redaction pour les vues acheteur et prestataire). */}
        <Banner
          variant="info"
          className="bc-mention-procedural"
          message={MENTION_VALIDITE_ACCORD_CADRE_BC}
        />
        <div className="section-content">
          <div className="bcc-desc">
            <div className="bcc-desc__grid">
              <ChampRO label="Accord-cadre en vigueur" value="Accord-cadre édition 2025-2026" col={3} />
              <ChampRO label="Date de validité" value="01/01/2025 au 31/12/2026" col={3} />
              <ChampRO label="Lot technique" value={lotTechnique} col={3} />
              <ChampRO label="Lot géographique" value={lotGeographique} col={3} />
              <ChampRO label="Date et heure limite de réception des offres" value={dateTimeFr(commande.expire_at)} col={3} />
              <ChampRO label="Durée de consultation (jours calendaires)" value="10 jours" col={3} />
              <ChampRO label="Sous-traitance autorisée" value="DC4 à présenter après notification" col={3} />
            </div>

            {/* Titulaire du marche : UNIQUEMENT le prestataire de cette commande (son entite, commande.id_entite),
                pas les 3 titulaires du lot. mode="BC" + entite -> une seule carte ; le rang ne sert qu'a la
                pastille, l'annuaire pouvant avoir change de titulaire pour ce rang depuis la commande. */}
            <div className="bcc-desc__sous-section" style={{ marginTop: '2rem' }}>
              <div className="form-group">
                <label>Titulaire du marché (votre rang)</label>
                <TitulairesLotCards
                  lotTechnique={op.lot_technique?.id}
                  departement={departement?.id}
                  mode="BC"
                  rang={rang}
                  entiteId={commande.id_entite}
                />
              </div>
            </div>

            {/* Documents liés à la consultation : pièces de l'accord-cadre (CCTP...) cliquables + à droite
                la liste des pièces de la documentation technique (miroir read-only), comme côté MS. */}
            <div className="bcc-desc__sous-section" style={{ marginTop: '2rem' }}>
              <div className="form-group">
                <label>
                  Documents liés à la consultation <span style={{ color: 'red' }}>*</span>
                </label>
                {acDocsLoading && piecesAccordCadre.length === 0 ? (
                  <div className="input-inactif">Chargement des pièces…</div>
                ) : (
                  <DocumentsList
                    documents={piecesAccordCadre}
                    variant="grid"
                    // Une seule place, comme un document : la carte reste sur la ligne des autres.
                    trailingCardSpan={1}
                    trailingCard={
                      <ListePiecesCard
                        texte={op.liste_pieces_documentation}
                        placeholder={PLACEHOLDER}
                      />
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </SectionPliable>

      {inlineError && (
        <div className="cmd-consultation__inline-message cmd-consultation__inline-message--error">
          {inlineError}
        </div>
      )}

      {/* Rappel de l'echeance ET de l'etat juste avant les boutons d'action (memes bandeaux qu'en
          haut, definis une seule fois) : l'onglet est long, la decision se prend ici. */}
      {depotBanner}
      {stateBanners}

      {/* Boutons d'actions presta. La signature (accept panier normal / transfer panier inverse)
          ET la composition du catalogue vivent dans l'onglet OFFRE : ici on ouvre le refus et on
          renvoie vers l'onglet Offre pour faire / voir l'offre. */}
      <div className="cmd-consultation__actions">
        {/* Le refus vient AVANT "Imprimer la consultation". */}
        {(showAccepterRefuser || isChiffrage) && !hasActed && (
          <Button
            value={isChiffrage ? 'Refuser de faire une offre' : "Refuser l'opération"}
            variant="cancel"
            onClick={() => {
              setInlineError(null);
              setRefuseSuccess(false);
              setIsRefusing(false);
              setIsRefuseModalOpen(true);
            }}
          />
        )}

        <PrintButton
          targetRef={printRef}
          fileName={`${op.numero || commande.id} - Consultation Rang ${rang}`}
          value="Imprimer la consultation"
          suiviHeaderProps={{
            titre: op.nom || 'Commande BC',
            sousTitre: '',
            subtitle,
            status: commande.status?.nom || 'Statut inconnu',
            statusColor: getStatusColor(commande.status?.nom || ''),
            delais: commande.expire_at ?? undefined,
            backgroundImage: lotImage,
          }}
        />

        <NextTabButton
          label={nextTabCta.label}
          hidden={nextTabCta.hidden}
          // `no-print` : de la navigation n'a rien a faire sur un document imprime, et `printRef`
          // couvre tout l'onglet (barre d'actions comprise).
          className="no-print"
          onClick={() => onGoToOffre?.()}
        />
      </div>

      {/* Confirmation de refus -> message succes -> refetch (pas de reload) */}
      <ValidationPopUp
        isOpen={isRefuseModalOpen}
        title={refuseSuccess ? 'Opération refusée' : "Refuser l'opération"}
        text={
          refuseSuccess
            ? 'Opération refusée avec succès.'
            : 'Êtes-vous sûr de vouloir refuser cette opération ? Cette action est irréversible.'
        }
        isLoading={isRefusing}
        onClose={refuseSuccess ? handleCloseRefusSuccess : () => setIsRefuseModalOpen(false)}
        onConfirm={refuseSuccess ? handleCloseRefusSuccess : handleConfirmRefus}
        cancelLabel={refuseSuccess ? undefined : 'Annuler'}
        confirmLabel={refuseSuccess ? 'Fermer' : 'Oui, refuser'}
      />
    </div>
  );
};

export default CommandeConsultation;
